import { useEffect, useRef, useState } from 'react';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';
import { chicagoTodayIso } from '../../../utils/weekDates.js';

interface LockProjectDialogProps {
  projectName: string | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (lockedAt: string) => void;
  isPending: boolean;
}

export function LockProjectDialog({
  projectName,
  open,
  onClose,
  onSubmit,
  isPending,
}: LockProjectDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  // Chicago's day, not UTC's. As the default value for the lock date this is what an
  // administrator accepts without editing, and the UTC day is already tomorrow between
  // 19:00 and midnight Central — so an evening lock silently dated itself a day late.
  // Nothing rejects it: the input has no max and the server only checks the format.
  const today = chicagoTodayIso();
  const [lockedAt, setLockedAt] = useState(today);

  useEffect(() => {
    if (!open) return;
    setLockedAt(today);

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, today]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!lockedAt) return;
    onSubmit(lockedAt);
  }

  return (
    <div className="dialog-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lock-project-dialog-title"
        className="projects-dialog"
        style={{ maxWidth: 420 }}
      >
        <h2 id="lock-project-dialog-title" className="dialog-title">
          Lock at cutoff
        </h2>
        {projectName && (
          <p
            style={{ margin: '0 0 var(--space-4)', color: 'var(--fg3)', fontSize: 'var(--fs-14)' }}
          >
            {projectName}
          </p>
        )}

        <div className="dialog-delete-warning" style={{ marginBottom: 'var(--space-4)' }}>
          After locking, no time entries can be added or edited for this project for dates after the
          cutoff.
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dialog-form-field">
            <label htmlFor="lock-date">Lock at date</label>
            <input
              id="lock-date"
              type="date"
              value={lockedAt}
              onChange={(e) => setLockedAt(e.target.value)}
              required
              aria-required="true"
            />
          </div>

          <div className="dialog-footer">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!lockedAt || isPending}
              aria-busy={isPending}
            >
              {isPending ? 'Locking…' : 'Lock project'}
            </button>
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
